<template>
  <div class="fu-toolbar" :class="`fu-toolbar--${layout}`">
    <label class="fu-toolbar__search">
      <span class="fu-toolbar__sr">Search</span>
      <fu-icon name="search" :size="16" class="fu-toolbar__search-icon" />
      <input
        type="search"
        class="fu-toolbar__search-input"
        :value="search"
        :placeholder="placeholder"
        autocomplete="off"
        data-test="fu-search"
        @input="$emit('update:search', $event.target.value)"
      />
    </label>

    <slot name="filters" />

    <div ref="statusWrap" class="fu-toolbar__status">
      <button
        ref="statusButton"
        type="button"
        class="fu-toolbar__btn"
        :class="{ 'fu-toolbar__btn--active': statuses.length > 0 }"
        :aria-expanded="statusOpen ? 'true' : 'false'"
        aria-haspopup="true"
        data-test="fu-status-filter"
        @click="toggleStatus"
      >
        <fu-icon name="alert" :size="16" />
        <span>{{ statusButtonLabel }}</span>
        <fu-icon name="chev" :size="15" class="fu-toolbar__chev" />
      </button>

      <div
        v-if="statusOpen"
        class="fu-status-pop"
        role="dialog"
        aria-label="Filter by status"
        data-test="fu-status-popover"
      >
        <div class="fu-status-pop__head">
          <span class="fu-status-pop__title">Status</span>
          <button type="button" class="fu-status-pop__clear" :disabled="!statuses.length" @click="clearStatuses">Clear</button>
        </div>
        <div v-if="!statusOptions.length" class="fu-status-pop__empty">No statuses in this list.</div>
        <label
          v-for="o in statusOptions"
          :key="o.label"
          class="fu-status-pop__option"
          data-test="fu-status-option"
        >
          <input
            type="checkbox"
            class="fu-status-pop__check"
            :checked="statuses.indexOf(o.label) !== -1"
            @change="toggleOption(o.label)"
          />
          <span class="fu-status-pop__dot" :class="`fu-status-pop__dot--${o.category || 'none'}`" aria-hidden="true"></span>
          <span class="fu-status-pop__label">{{ o.label }}</span>
          <span class="fu-status-pop__count">{{ o.count }}</span>
        </label>
      </div>
    </div>

    <slot name="filters-end" />

    <label v-if="layout === 'phone' && sortOptions.length" class="fu-toolbar__sort">
      <span class="fu-toolbar__sr">Sort</span>
      <select
        class="fu-toolbar__sort-select"
        :value="sortKey"
        data-test="fu-sort-select"
        @change="$emit('update:sort', $event.target.value)"
      >
        <option value="">Sort: default</option>
        <option v-for="o in sortOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
      </select>
    </label>

    <button
      v-if="anyFilter"
      type="button"
      class="fu-toolbar__clear"
      data-test="fu-clear-filters"
      @click="$emit('clear')"
    >
      <fu-icon name="x" :size="16" />
      Clear
    </button>
  </div>
</template>

<script>
/**
 * The row under the view chips: search, the page's own filter buttons (`filters` slot), the
 * Status filter with its checkbox popover (counts + Clear), the page's trailing buttons
 * (`filters-end` slot — "More filters"), the phone-only Sort select and the "Clear" link. Presentational — every change is an event; the queue owns the state.
 */
import FuIcon from './FuIcon.vue';

export default {
  name: 'FollowupsToolbar',
  components: { FuIcon },
  props: {
    search: { type: String, default: '' },
    placeholder: { type: String, default: 'Search…' },
    statuses: { type: Array, default: () => [] },        // the picked status labels
    statusOptions: { type: Array, default: () => [] },   // [{ label, count, category }]
    layout: { type: String, default: 'desktop' },
    sortOptions: { type: Array, default: () => [] },     // [{ value, label }] — phone Sort select
    sortKey: { type: String, default: '' },
    anyFilter: { type: Boolean, default: false },
  },

  data() {
    return { statusOpen: false };
  },

  computed: {
    statusButtonLabel() {
      return this.statuses.length ? `Status · ${this.statuses.length}` : 'Status';
    },
  },

  beforeDestroy() {
    this.unlisten();
  },

  methods: {
    toggleStatus() {
      if (this.statusOpen) this.closeStatus();
      else this.openStatus();
    },
    openStatus() {
      this.statusOpen = true;
      document.addEventListener('mousedown', this.onDocPointer, true);
      document.addEventListener('touchstart', this.onDocPointer, true);
      document.addEventListener('keydown', this.onKey, true);
    },
    closeStatus(restoreFocus) {
      this.statusOpen = false;
      this.unlisten();
      if (restoreFocus && this.$refs.statusButton) this.$refs.statusButton.focus();
    },
    unlisten() {
      document.removeEventListener('mousedown', this.onDocPointer, true);
      document.removeEventListener('touchstart', this.onDocPointer, true);
      document.removeEventListener('keydown', this.onKey, true);
    },
    onDocPointer(e) {
      const wrap = this.$refs.statusWrap;
      if (wrap && !wrap.contains(e.target)) this.closeStatus(false);
    },
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') this.closeStatus(true);
    },
    toggleOption(label) {
      const i = this.statuses.indexOf(label);
      const next = this.statuses.slice();
      if (i === -1) next.push(label);
      else next.splice(i, 1);
      this.$emit('update:statuses', next);
    },
    clearStatuses() {
      this.$emit('update:statuses', []);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.fu-toolbar__sr {
  @include fu-visually-hidden;
}

.fu-toolbar__search {
  position: relative;
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 320px;
  margin: 0;
}

.fu-toolbar__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: $fu-text-muted;
  pointer-events: none;
}

.fu-toolbar__search-input {
  @include fu-input;
  padding-left: 36px;
  // The browser's own clear "x" on type=search collides with the design; the Clear link does it.
  &::-webkit-search-cancel-button { -webkit-appearance: none; }
}

.fu-toolbar__btn {
  @include fu-btn(secondary, md);
  .fu-icon { color: $fu-text-muted; }
}

.fu-toolbar__btn--active {
  border-color: $fu-brand-300;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.fu-toolbar__chev {
  margin-left: -2px;
}

.fu-toolbar__status {
  position: relative;
}

.fu-status-pop {
  @include fu-popover-surface;
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: $fu-z-popover;
  width: 300px;
  max-width: calc(100vw - 32px);
  max-height: 360px;
  padding: 14px $fu-space-4;
}

.fu-status-pop__head {
  display: flex;
  align-items: center;
  margin-bottom: $fu-space-3;
}

.fu-status-pop__title {
  @include fu-font-display;
  font-weight: 800;
  font-size: 15px;
}

.fu-status-pop__clear {
  @include fu-link-btn;
  margin-left: auto;
  font-size: 12.5px;
  &[disabled] { color: $fu-text-subtle; cursor: default; text-decoration: none; }
}

.fu-status-pop__empty {
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-status-pop__option {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  margin: 0;
  padding: 5px 0;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  cursor: pointer;
}

.fu-status-pop__check {
  @include fu-checkbox;
}

.fu-status-pop__dot {
  @include fu-status-dot;
  @include fu-status-dot-colors;
}

.fu-status-pop__label {
  min-width: 0;
  @include fu-ellipsis;
}

.fu-status-pop__count {
  @include fu-tabular;
  margin-left: auto;
  font-size: 12.5px;
  font-weight: $fu-fw-regular;
  color: $fu-text-subtle;
}

.fu-toolbar__sort-select {
  @include fu-input;
  width: 180px;
  padding-right: 30px;
  cursor: pointer;
}

.fu-toolbar__sort {
  margin: 0;
}

.fu-toolbar__clear {
  @include fu-link-btn;
  margin-left: auto;
  font-size: 13px;
  padding: 4px 2px;
}

// Phone: the search box takes the whole first line; the popover opens full-width under its button.
.fu-toolbar--phone {
  .fu-toolbar__search { flex: 1 1 100%; max-width: 100%; }
  .fu-toolbar__search-input,
  .fu-toolbar__sort-select { font-size: 16px; }
}
</style>
